import { useTheme } from '@/contexts/ThemeContext';
import { TextCanvas } from '../TextCanvas';

export default function PrivacyPolicy(): JSX.Element {
  const host = 'liernlimited.com';
  const email = host ? `contact@${host}` : '';
  const theme = useTheme();
  const { landingPage } = theme;
  return (
    <>
      <section className="mb-8">
        <h1 className="text-2xl font-bold mb-4">
          Welcome to our privacy policy of our website (&quot;we,&quot;
          &quot;us,&quot; or &quot;our&quot;).
        </h1>
        <p>
          We highly value your privacy and are dedicated to safeguarding your
          personal information. This privacy policy is designed to provide you
          with insights into how we manage your personal data when you access
          our website, irrespective of your location, and to elucidate your
          privacy rights as well as the legal protections in place. If you have
          any inquiries concerning our data policy, please don&apos;t hesitate
          to reach out to us via email at {email}.
        </p>

        <div className="my-4">
          <h2 className="text-xl font-bold mb-2">
            IMPORTANT INFORMATION AND WHO WE ARE
          </h2>
          <h3 className="text-lg font-semibold mb-2">
            Purpose of this privacy policy
          </h3>
          <p>
            The primary purpose of this privacy policy is to furnish you with
            details about how we gather and process your personal data during
            your interactions with this website. This encompasses any
            information you may furnish to us while utilizing this website, such
            as when you acquire our services.
          </p>
          <p>
            Our website is not intended for use by children, and we do not
            intentionally collect data related to minors.
          </p>
          <p>
            It is essential for you to review this privacy policy alongside any
            other privacy or fair processing policies we may provide on specific
            occasions when we gather or process your personal data. This ensures
            that you are fully informed about the reasons behind the collection
            and use of your data. This privacy policy complements other notices
            and privacy policies and is not intended to supersede them.
          </p>
        </div>

        <div className="mb-4">
          <h3 className="text-lg font-semibold mb-2">Controller</h3>
          <p>
            We are the data controller and bear responsibility for your personal
            data. We have designated a data privacy manager who supervises
            queries related to this privacy policy. If you have any questions
            about this privacy policy or wish to exercise your legal rights,
            please contact the data privacy manager using the contact details
            provided below.
          </p>

          <h3 className="text-lg font-semibold mb-2">Contact details</h3>
          <p>
            For inquiries concerning this privacy policy or our privacy
            practices, you may contact our data privacy manager via email at{' '}
            {email} or by sending a written message to:{' '}
            <TextCanvas
              text="Liern Ltd, Leonidou, 8, 2007, Nicosia, Cyprus"
              textColor="#000"
              inline
            />
          </p>
          <p>
            Changes to the privacy policy and your duty to inform us of changes
          </p>
          <p>
            We routinely review and update our privacy policy. The date of the
            last update is specified at the end of this policy, and historical
            versions can be obtained by contacting us.
          </p>
          <p>
            It is vital for us to possess accurate and current personal data
            about you. Therefore, please keep us informed if there are any
            changes to your personal data during your engagement with us.
          </p>
        </div>
      </section>
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4">Third-Party Links</h2>
        <p>
          Our website may contain links to third-party websites, plugins, and
          applications, including those related to payment providers. Clicking
          on these links or enabling these connections may result in third
          parties collecting or sharing data about you. We do not govern these
          third-party websites and are not responsible for their privacy
          statements. When you depart from our website, we encourage you to
          review the privacy policy of each website you visit.
        </p>

        <div className="mb-8">
          <h2 className="text-2xl font-bold mb-4">
            THE DATA WE COLLECT ABOUT YOU
          </h2>
          <p>
            Personal data, or personal information, refers to any information
            that can identify an individual. This definition excludes data where
            the individual&apos;s identity has been removed, rendering it
            anonymous.
          </p>

          <div className="mb-4">
            <h3 className="text-xl font-semibold mb-2">
              Categories of Personal Data We Collect
            </h3>
            <ol className="list-decimal pl-6">
              <li>
                Identity Data: This includes your first name, maiden name, last
                name, username or a similar identifier, marital status, title,
                date of birth, gender, driving license number and its expiration
                date, and national insurance number.
              </li>
              <li>
                Contact Data: This category covers your billing address,
                delivery address, email address, and telephone numbers.
              </li>
              <li>
                Transaction Data: We gather details about the products and
                services you have purchased from us.
              </li>
              <li>
                Technical Data: This type of data encompasses your internet
                protocol (IP) address, login data, browser type and version,
                time zone settings, location information, browser plug-in types
                and versions, operating system and platform, and other
                technological details related to the devices you use to access
                our website.
              </li>
              <li>
                Profile Data: Profile Data includes information such as your
                username and password, records of purchases or orders made by
                you, your interests, preferences, feedback, and responses to
                surveys.
              </li>
              <li>
                Usage Data: This category involves information about how you
                utilize our website, products, and services.
              </li>
              <li>
                Marketing and Communications Data: It pertains to your
                preferences regarding marketing communications from us and third
                parties, as well as your communication preferences.
              </li>
            </ol>
          </div>

          <div className="mb-4">
            <h3 className="text-xl font-semibold mb-2">
              Financial Data Processing
            </h3>
            <p>
              We engage a third-party to collect and process your financial
              data, including your bank account or payment card details when
              applicable. This third party verifies the success of your payment
              and subsequently transfers the payment to us.
            </p>
          </div>

          <div className="mb-4">
            <h3 className="text-xl font-semibold mb-2">Aggregated Data</h3>
            <p>
              Additionally, we collect, use, and share &quot;Aggregated
              Data,&quot; which refers to statistical or demographic data used
              for various purposes. Aggregated Data may be derived from your
              personal data but is not considered personal data under the law,
              as it does not directly or indirectly reveal your identity. For
              example, we may aggregate your Usage Data to calculate the
              percentage of users accessing a specific website feature. However,
              if we combine or link Aggregated Data with your personal data in a
              way that allows for your direct or indirect identification, we
              treat the combined data as personal data, subject to this privacy
              policy.
            </p>
          </div>

          <div className="mb-4">
            <h3 className="text-xl font-semibold mb-2">
              Collection of Special Categories of Personal Data
            </h3>
            <p>
              In the course of providing our services, we may collect
              &quot;Special Categories of Personal Data&quot; about you. This
              may include information related to your race or ethnicity,
              religious or philosophical beliefs, sex life, sexual orientation,
              political opinions, trade union membership, details about your
              health, and genetic and biometric data. We may also collect
              information regarding criminal convictions and offenses. It&apos;s
              important to note that we do not retain this data once it has been
              forwarded to you.
            </p>
          </div>

          <div className="mb-4">
            <h3 className="text-xl font-semibold mb-2">
              Impact of Not Providing Personal Data
            </h3>
            <p>
              In instances where we are legally obligated to collect personal
              data or as stipulated in a contractual agreement with you, failure
              to provide the requested data may result in us being unable to
              provide you with the services for which you have made payment. In
              such cases, we may need to cancel the service, and we will notify
              you if this becomes necessary.
            </p>
          </div>
        </div>

        <div className="mb-8">
          <h2 className="text-2xl font-bold mb-4">
            HOW IS YOUR PERSONAL DATA COLLECTED
          </h2>
          <p>
            We employ various methods to collect data from and about you,
            including the following:
          </p>

          <div className="mb-4">
            <h3 className="text-xl font-semibold mb-2">
              Information You Provide
            </h3>
            <p>
              This encompasses data about yourself that you willingly provide to
              us. You may furnish your Identity, Contact, and Financial Data by
              completing forms on our website or engaging in communication with
              us via phone, email, or other means. This includes personal data
              you supply when applying for our products or services, creating an
              account on our website, requesting marketing materials, or
              providing feedback or contacting us.
            </p>
          </div>

          <div className="mb-4">
            <h3 className="text-xl font-semibold mb-2">
              Information We Automatically Collect
            </h3>
            <p>
              As you interact with our website, we automatically gather
              Technical Data pertaining to your device, browsing actions, and
              usage patterns. We acquire this personal data through the use of
              cookies, server logs, and similar technologies. Additionally, if
              you visit other websites utilizing our cookies, we may also
              receive Technical Data from those sources. Further information
              regarding cookies is available in the &quot;Cookies&quot; section
              below.
            </p>
          </div>

          <div className="mb-4">
            <h3 className="text-xl font-semibold mb-2">
              Information from Third Parties
            </h3>
            <p>
              We obtain personal data about you from various third-party sources
              and public references, as detailed below:
            </p>
            <ul className="list-disc pl-6">
              <li>
                Technical Data from the following parties:
                <ul className="list-disc pl-6">
                  <li>
                    Analytics providers like Google, located outside the UK.
                  </li>
                  <li>
                    Advertising networks such as DoubleClick or Google, situated
                    outside the UK.
                  </li>
                  <li>
                    Search information providers like Google or Bing, operating
                    outside the UK.
                  </li>
                  {landingPage === 'v13' ? (
                    <li>
                      Contact, Financial, and Transaction Data from providers of
                      technical, payment, and delivery services, such as Stripe
                      or Checkout, which may be headquartered outside the UK.
                    </li>
                  ) : (
                    <li>
                      Contact, Financial, and Transaction Data from providers of
                      technical, payment, and delivery services, which may be
                      headquartered outside the UK.
                    </li>
                  )}
                  <li>
                    Identity and Contact Data from publicly available sources
                    like the Government&apos;s driving license information site,
                    the Driver and Vehicle Licensing Agency, Companies House,
                    and the Electoral Register, all located within the UK.
                  </li>
                </ul>
              </li>
            </ul>
            <p>
              It&apos;s important to note that the geographical locations
              mentioned are provided for reference and may vary based on the
              specific circumstances and services utilized.
            </p>
          </div>
        </div>

        <div>
          <h2 className="text-2xl font-bold mb-4">
            HOW WE USE YOUR PERSONAL DATA
          </h2>
          <p>
            We will solely employ your personal data when it is permissible by
            law. Most frequently, we employ your personal data in the following
            situations:
          </p>

          <div className="mb-4">
            <h3 className="text-xl font-semibold mb-2">
              Contractual Necessity
            </h3>
            <p>
              We use your personal data when it is necessary to perform the
              contract we are about to enter into or have already entered into
              with you for the provision of services.
            </p>
          </div>

          <div className="mb-4">
            <h3 className="text-xl font-semibold mb-2">Legitimate Interests</h3>
            <p>
              We process your personal data when it is essential for our
              legitimate interests (or those of a third party), and your
              interests and fundamental rights do not override these interests.
              This means we consider it in the interest of our business to
              conduct and manage our operations to provide you with the best
              service and the most secure experience possible. We meticulously
              weigh the potential impact on you, both positive and negative, as
              well as your rights, before processing your personal data for our
              legitimate interests. We do not use your personal data for
              activities where the impact on you overrides our interests (unless
              we have your consent or are obligated or permitted to do so by
              law). If you would like further information on how we assess our
              legitimate interests in relation to specific activities, please
              reach out to us.
            </p>
          </div>

          <div className="mb-4">
            <h3 className="text-xl font-semibold mb-2">Legal Obligations</h3>
            <p>
              We employ your personal data when it is required to comply with a
              legal obligation that we are subject to.
            </p>
          </div>

          <div className="mb-4">
            <p>
              While we typically do not rely on consent as a legal basis for
              processing your personal data, we will seek your consent before
              sending you third-party direct marketing communications via email
              or text message. You have the right to withdraw your consent for
              marketing activities at any time by contacting us.
            </p>
          </div>

          <div className="mb-4">
            <h2 className="text-2xl font-bold mb-2">
              Purposes for which we will use your personal data
            </h2>
            <p>
              Below is a table outlining the various ways we intend to use your
              personal data, along with the legal bases we rely on for each
              purpose. When multiple legal grounds are applicable for a specific
              purpose, please feel free to contact us for details regarding the
              specific legal basis we are relying on.
            </p>
            <p>
              Please do not hesitate to reach out to us if you require
              clarification on the specific legal grounds we are using to
              process your personal data when more than one ground is identified
              in the table below.
            </p>
          </div>
        </div>

        <table className="text-left my-3 min-w-full divide-gray-200 border border-gray-200 leading-4 divide-y">
          <thead className="text-gray-900 tracking-wider font-medium">
            <tr className="divide-x">
              <th className="px-6 py-3 w-4/12">Purpose/Activity</th>
              <th className="px-6 py-3 w-3/12">Type of data</th>
              <th className="px-6 py-3 w-5/12">
                Lawful basis for processing including basis of legitimate
                interest
              </th>
            </tr>
          </thead>
          <tbody className="bg-white text-sm leading-5 font-medium divide-y">
            <tr className="divide-x">
              <td className="px-6 py-4 w-4/12">
                To register you as a new customer
              </td>
              <td className="px-6 py-4 w-3/12">
                <ul className="list-lower-alpha px-6">
                  <li>Identity</li>
                  <li>Contact</li>
                </ul>
              </td>
              <td className="px-6 py-4 w-5/12">
                Performance of a contract with you
              </td>
            </tr>
            <tr className="divide-x">
              <td className="px-6 py-4 w-4/12">
                To process and deliver your order including:
                <ul className="list-lower-alpha px-6">
                  <li>Manage payments, fees and charges</li>
                  <li>Collect and recover money owed to us</li>
                </ul>
              </td>
              <td className="px-6 py-4 w-3/12">
                <ul className="list-lower-alpha px-6">
                  <li>Identity</li>
                  <li>Contact</li>
                  <li>Financial</li>
                  <li>Transaction</li>
                  <li>Marketing and Communications</li>
                </ul>
              </td>
              <td className="px-6 py-4 w-5/12">
                <ul className="list-lower-alpha px-6">
                  <li>Performance of a contract with you</li>
                  <li>
                    Necessary for our legitimate interests (to recover debts due
                    to us)
                  </li>
                </ul>
              </td>
            </tr>
            <tr className="divide-x">
              <td className="px-6 py-4 w-4/12">
                To manage our relationship with you which will include:
                <ul className="list-lower-alpha px-6">
                  <li>
                    Notifying you about changes to our terms or privacy policy
                  </li>
                  <li>Asking you to leave a review or take a survey</li>
                </ul>
              </td>
              <td className="px-6 py-4 w-3/12">
                <ul className="list-lower-alpha px-6">
                  <li>Identity</li>
                  <li>Contact</li>
                  <li>Profile</li>
                  <li>Marketing and Communications</li>
                </ul>
              </td>
              <td className="px-6 py-4 w-5/12">
                <ul className="list-lower-alpha px-6">
                  <li>Performance of a contract with you</li>
                  <li>Necessary to comply with a legal obligation</li>
                  <li>
                    Necessary for our legitimate interests (to keep our records
                    updated and to study how customers use our
                    products/services)
                  </li>
                </ul>
              </td>
            </tr>
            <tr className="divide-x">
              <td className="px-6 py-4 w-4/12">
                To enable you to partake in a prize draw, competition or
                complete a survey
              </td>
              <td className="px-6 py-4 w-3/12">
                <ul className="list-lower-alpha px-6">
                  <li>Identity</li>
                  <li>Contact</li>
                  <li>Profile</li>
                  <li>Usage</li>
                  <li>Marketing and Communications</li>
                </ul>
              </td>
              <td className="px-6 py-4 w-5/12">
                <ul className="list-lower-alpha px-6">
                  <li>Performance of a contract with you</li>
                  <li>
                    Necessary for our legitimate interests (to study how
                    customers use our products/services, to develop them and
                    grow our business)
                  </li>
                </ul>
              </td>
            </tr>
            <tr className="divide-x">
              <td className="px-6 py-4 w-4/12">
                To administer and protect our business and this website
                (including troubleshooting, data analysis, testing, system
                maintenance, support, reporting and hosting of data)
              </td>
              <td className="px-6 py-4 w-3/12">
                <ul className="list-lower-alpha px-6">
                  <li>Identity</li>
                  <li>Contact</li>
                  <li>Technical</li>
                </ul>
              </td>
              <td className="px-6 py-4 w-5/12">
                <ul className="list-lower-alpha px-6">
                  <li>
                    Necessary for our legitimate interests (for running our
                    business, provision of administration and IT services,
                    network security, to prevent fraud and in the context of a
                    business reorganisation or group restructuring exercise)
                  </li>
                  <li>Necessary to comply with a legal obligation</li>
                </ul>
              </td>
            </tr>
            <tr className="divide-x">
              <td className="px-6 py-4 w-4/12">
                To deliver relevant website content and advertisements to you
                and measure or understand the effectiveness of the advertising
                we serve to you
              </td>
              <td className="px-6 py-4 w-3/12">
                <ul className="list-lower-alpha px-6">
                  <li>Identity</li>
                  <li>Contact</li>
                  <li>Profile</li>
                  <li>Usage</li>
                  <li>Marketing and Communications</li>
                  <li>Technical</li>
                </ul>
              </td>
              <td className="px-6 py-4 w-5/12">
                Necessary for our legitimate interests (to study how customers
                use our products/services, to develop them, to grow our business
                and to inform our marketing strategy)
              </td>
            </tr>
            <tr className="divide-x">
              <td className="px-6 py-4 w-4/12">
                To use data analytics to improve our website, products/services,
                marketing, customer relationships and experiences
              </td>
              <td className="px-6 py-4 w-3/12">
                <ul className="list-lower-alpha px-6">
                  <li>Technical</li>
                  <li>Usage</li>
                </ul>
              </td>
              <td className="px-6 py-4 w-5/12">
                Necessary for our legitimate interests (to define types of
                customers for our products and services, to keep our website
                updated and relevant, to develop our business and to inform our
                marketing strategy)
              </td>
            </tr>
            <tr className="divide-x">
              <td className="px-6 py-4 w-4/12">
                To make suggestions and recommendations to you about goods or
                services that may be of interest to you
              </td>
              <td className="px-6 py-4 w-3/12">
                <ul className="list-lower-alpha px-6">
                  <li>Identity</li>
                  <li>Contact</li>
                  <li>Technical</li>
                  <li>Usage</li>
                  <li>Profile</li>
                  <li>Marketing and Communications</li>
                </ul>
              </td>
              <td className="px-6 py-4 w-5/12">
                Necessary for our legitimate interests (to develop our
                products/services and grow our business)
              </td>
            </tr>
          </tbody>
        </table>
      </section>
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4">Marketing</h2>
        <p>
          We are committed to offering you choices when it comes to certain uses
          of your personal data, especially in the realm of marketing and
          advertising. You have the option to reach out to us regarding our
          marketing practices.
        </p>

        <div className="mb-4">
          <h3 className="text-xl font-semibold mb-2">
            Promotional Offers from Us
          </h3>
          <p>
            We may utilize your Identity, Contact, Technical, Usage, and Profile
            Data to form an understanding of what we believe you may want or
            require, as well as what might be of interest to you. This is how we
            determine which products, services, and offers could be pertinent to
            you (referred to as &quot;marketing&quot;). You will receive
            marketing communications from us if you have requested information
            from us or have purchased services from us and have not opted out of
            receiving such marketing.
          </p>
        </div>

        <div className="mb-4">
          <h3 className="text-xl font-semibold mb-2">Third-Party Marketing</h3>
          <p>
            Before sharing your personal data with any third party for marketing
            purposes, we will seek your explicit opt-in consent.
          </p>
        </div>

        <div className="mb-4">
          <h3 className="text-xl font-semibold mb-2">Opting Out</h3>
          <p>
            You have the ability to request that we or third parties cease
            sending you marketing messages at any time. You can do this by
            following the opt-out links provided in any marketing messages you
            receive or by contacting us directly. It&apos;s important to note
            that opting out of marketing messages does not affect the personal
            data you have provided to us as a result of service purchases,
            warranty registrations, service experiences, or other transactions.
          </p>
        </div>

        <div className="mb-8">
          <h2 className="text-2xl font-bold mb-4">Cookies</h2>
          <p>
            You can configure your web browser to reject all or some browser
            cookies or to notify you when websites attempt to set or access
            cookies. Please be aware that disabling or refusing cookies may
            result in certain parts of this website becoming inaccessible or
            functioning improperly. For more information regarding the cookies
            we use, please refer to the &quot;Cookies&quot; section below.
          </p>
        </div>

        <div className="mb-8">
          <h2 className="text-2xl font-bold mb-4">Change of Purpose</h2>
          <p>
            We will exclusively use your personal data for the purposes for
            which it was originally collected unless we reasonably determine
            that we need to use it for another purpose that is compatible with
            the initial one. If you seek an explanation regarding how the
            processing for a new purpose aligns with the original purpose,
            please reach out to us.
          </p>
          <p>
            Should we require the use of your personal data for an unrelated
            purpose, we will notify you and elucidate the legal basis permitting
            such usage. Please be aware that we may process your personal data
            without your knowledge or consent, adhering to the aforementioned
            principles, when mandated or permitted by applicable law.
          </p>
        </div>

        <div className="mb-8">
          <h2 className="text-2xl font-bold mb-4">
            DISCLOSURES OF YOUR PERSONAL DATA
          </h2>
          <p>
            We may share your personal data with the parties set out below for
            the purposes set out in the table “Purposes” for which we will use
            your personal data above.
          </p>
          <ul className="list-disc pl-6">
            <li>
              Driver and Vehicle Licensing Agency (DVLA): Acting as processors
              or joint controllers, based in the UK, the DVLA will process your
              driving license application.
            </li>
            {landingPage === 'v13' ? (
              <li>
                Third-Party Payment Processors: We may engage third-party
                payment processors like Stripe or Checkout.
              </li>
            ) : (
              <li>
                Third-Party Payment Processors: We may engage third-party
                payment processors.
              </li>
            )}

            <li>
              Affiliated Companies: Other entities within our corporate group,
              serving as joint controllers or processors, based in the UK, and
              providing IT and system administration services and conducting
              leadership reporting.
            </li>
            <li>
              Service Providers: Acting as processors, these service providers
              are based in the UK or the European Economic Area (EEA) and offer
              IT and system administration services.
            </li>
            {landingPage === 'v13' && (
              <li>
                Professional Advisers: Serving as processors or joint
                controllers, including lawyers, bankers, auditors, and insurers,
                these professionals are based in the UK and provide consultancy,
                banking, legal, insurance, and accounting services.
              </li>
            )}
            <li>
              HM Revenue & Customs, Regulators, and Authorities: Acting as
              processors or joint controllers, these entities are based in the
              UK and may require the reporting of processing activities in
              certain circumstances.
            </li>
            <li>
              Business Transfers: In the event that we decide to sell, transfer,
              or merge parts of our business or assets, or engage in business
              acquisitions or mergers, your personal data may be shared with
              third parties involved in such transactions. If there is a change
              in our business, the new owners may use your personal data in
              accordance with the terms outlined in this privacy policy.
            </li>
          </ul>
          <p>
            We obligate all third parties to uphold the security and legal
            treatment of your personal data. We do not grant our third-party
            service providers permission to utilize your personal data for their
            own purposes. Instead, they are only authorized to process your
            personal data for specified purposes in accordance with our
            instructions.
          </p>
        </div>

        <div className="mb-8">
          <h2 className="text-2xl font-bold mb-4">INTERNATIONAL TRANSFERS</h2>
          <p>
            We use third party payment providers and this may involve the
            transfer of your data outside the UK and/or the EEA. Where we use
            certain service providers, we may use specific contracts approved
            for use in the UK which give personal data the same protection it
            has in the UK such as the Standard Contractual Clauses. Please
            contact us if you want further information on the specific mechanism
            used by us when transferring your personal data out of the UK.
          </p>
        </div>

        <div className="mb-8">
          <h2 className="text-2xl font-bold mb-4">DATA SECURITY</h2>
          <p>
            We have put in place appropriate security measures to prevent your
            personal data from being accidentally lost, used or accessed in an
            unauthorised way, altered or disclosed. In addition, we limit access
            to your personal data to those employees, agents, contractors and
            other third parties who have a business need to know. They will only
            process your personal data on our instructions and they are subject
            to a duty of confidentiality.
          </p>
          <p>
            We have put in place procedures to deal with any suspected personal
            data breach and will notify you and any applicable regulator of a
            breach where we are legally required to do so.
          </p>
        </div>

        <div className="mb-8">
          <h2 className="text-2xl font-bold mb-4">DATA RETENTION</h2>
          <p>
            <span className="font-bold">
              How long will you use my personal data for
            </span>
            We will only retain your personal data for as long as reasonably
            necessary to fulfil the purposes we collected it for, including for
            the purposes of satisfying any legal, regulatory, tax, accounting or
            reporting requirements. We may retain your personal data for a
            longer period in the event of a complaint or if we reasonably
            believe there is a prospect of litigation in respect to our
            relationship with you.
          </p>
          <p>
            To determine the appropriate retention period for personal data, we
            consider the amount, nature and sensitivity of the personal data,
            the potential risk of harm from unauthorised use or disclosure of
            your personal data, the purposes for which we process your personal
            data and whether we can achieve those purposes through other means,
            and the applicable legal, regulatory, tax, accounting or other
            requirements.
          </p>
          <p>
            By law we have to keep basic information about our customers
            (including Contact, Identity, Financial and Transaction Data) for
            six years after they cease being customers.
          </p>
          <p>
            In some circumstances you can ask us to delete your data: see Your
            legal rights below for further information.
          </p>
          <p>
            In some circumstances we will anonymise your personal data (so that
            it can no longer be associated with you) for research or statistical
            purposes, in which case we may use this information indefinitely
            without further notice to you.
          </p>
          <p>
            All information from filled in applications that did not result in
            payment will be deleted within 48 hours.
          </p>
        </div>
      </section>
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4">YOUR LEGAL RIGHTS</h2>

        <p>
          Under certain circumstances, you have the following rights under data
          protection laws in relation to your personal data:
        </p>

        <ul className="list-disc pl-6 mb-4">
          <li>
            <strong>Request access to your personal data</strong> (commonly
            known as a &quot;data subject access request&quot;). This enables
            you to receive a copy of the personal data we hold about you and to
            check that we are lawfully processing it.
          </li>
          <li>
            <strong>Request correction</strong> of the personal data that we
            hold about you. This enables you to have any incomplete or
            inaccurate data we hold about you corrected, though we may need to
            verify the accuracy of the new data you provide to us.
          </li>
          <li>
            <strong>Request erasure</strong> of your personal data. This enables
            you to ask us to delete or remove personal data where there is no
            good reason for us continuing to process it. You also have the right
            to ask us to delete or remove your personal data where you have
            successfully exercised your right to object to processing (see
            below), where we may have processed your information unlawfully, or
            where we are required to erase your personal data to comply with
            local law. Note, however, that we may not always be able to comply
            with your request for erasure for specific legal reasons which will
            be notified to you, if applicable, at the time of your request.
          </li>
          <li>
            <strong>Object to processing</strong> of your personal data where we
            are relying on a legitimate interest (or those of a third party) and
            there is something about your particular situation which makes you
            want to object to processing on this ground as you feel it impacts
            on your fundamental rights and freedoms. You also have the right to
            object where we are processing your personal data for direct
            marketing purposes. In some cases, we may demonstrate that we have
            compelling legitimate grounds to process your information which
            override your rights and freedoms.
          </li>
          <li>
            <strong>Request restriction of processing</strong> of your personal
            data. This enables you to ask us to suspend the processing of your
            personal data in the following scenarios:
            <ul className="list-disc pl-6">
              <li>If you want us to establish the data&apos;s accuracy.</li>
              <li>
                Where our use of the data is unlawful but you do not want us to
                erase it.
              </li>
              <li>
                Where you need us to hold the data even if we no longer require
                it as you need it to establish, exercise or defend legal claims.
              </li>
              <li>
                You have objected to our use of your data but we need to verify
                whether we have overriding legitimate grounds to use it.
              </li>
            </ul>
          </li>
          <li>
            <strong>Request the transfer</strong> of your personal data to you
            or to a third party. We will provide to you, or a third party you
            have chosen, your personal data in a structured, commonly used,
            machine-readable format. Note that this right only applies to
            automated information which you initially provided consent for us to
            use or where we used the information to perform a contract with you.
          </li>
          <li>
            <strong>Withdraw consent</strong> at any time where we are relying
            on consent to process your personal data. However, this will not
            affect the lawfulness of any processing carried out before you
            withdraw your consent. If you withdraw your consent, we may not be
            able to provide certain products or services to you. We will advise
            you if this is the case at the time you withdraw your consent.
          </li>
        </ul>

        <p>
          If you wish to exercise any of the rights set out above, please
          contact us by email at <a href={`mailto:${email}`}>{email}</a>.
        </p>

        <p>
          <strong>No fee usually required:</strong> You will not have to pay a
          fee to access your personal data (or to exercise any of the other
          rights). However, we may charge a reasonable fee if your request is
          clearly unfounded, repetitive, or excessive. Alternatively, we could
          refuse to comply with your request in these circumstances.
        </p>

        <p>
          <strong>What we may need from you:</strong> We may need to request
          specific information from you to help us confirm your identity and
          ensure your right to access your personal data (or to exercise any of
          your other rights). This is a security measure to ensure that personal
          data is not disclosed to any person who has no right to receive it. We
          may also contact you to ask you for further information in relation to
          your request to speed up our response.
        </p>

        <p>
          <strong>Time limit to respond:</strong> We try to respond to all
          legitimate requests within one month. Occasionally it could take us
          longer than a month if your request is particularly complex or you
          have made a number of requests. In this case, we will notify you and
          keep you updated.
        </p>
      </section>
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4">COOKIES</h2>
        <p>
          We use cookies on our website to distinguish you from other users.
          This helps us provide you with a good experience when you browse our
          site and also allows us to improve our website.
        </p>

        <p>
          A cookie is a small file of letters and numbers that we store on your
          browser or the hard drive of your computer if you agree. Cookies
          contain information that is transferred to your computer’s hard drive.
        </p>

        <p>We use the following cookies:</p>

        <ul className="list-disc pl-6 mb-4">
          <li>
            <strong>Strictly necessary cookies:</strong> These are cookies that
            are required for the operation of our website. They include, for
            example, cookies that enable you to log into secure areas of our
            website, use a shopping cart, or make use of e-billing services.
          </li>
          <li>
            <strong>Analytical/performance cookies:</strong> They allow us to
            recognize and count the number of visitors and to see how visitors
            move around our website when they are using it. This helps us
            improve the way our website works, for example, by ensuring that
            users find what they are looking for easily.
          </li>
          <li>
            <strong>Functionality cookies:</strong> These are used to recognize
            you when you return to our website. This enables us to personalize
            our content for you, greet you by name, and remember your
            preferences (for example, your choice of language or region).
          </li>
          <li>
            <strong>Targeting/Analytics cookies:</strong> These cookies record
            your visit to our website, the pages you have visited, and the links
            you have followed. We will use this information to make our website
            and the advertising displayed on it more relevant to your interests.
            We may also share this information with third parties for this
            purpose. If you don’t want analytics cookies to be used when
            visiting our website then you can remove them by following these
            steps: (a) click ‘Tools’ and select ‘Internet Options’; (b) click
            the ‘Privacy’ tab and select ‘Advanced’; (c) click ‘Override
            automatic cookie handling’; and (d) specify how you wish to handle
            cookies from first and third party websites.
          </li>
          <li>
            <strong>Session cookies:</strong> These cookies store your browsing
            information and will be active until you leave our website and close
            your browser at which point they are erased. We use these to enable
            selected purchases to be carried over to subsequent web pages to
            speed up the process of your transaction.
          </li>
          <li>
            <strong>Persistent cookies:</strong> These cookies stay in one of a
            browser’s subfolders until they are deleted manually or your browser
            deletes them based on the duration period contained within the
            persistent cookie’s file. You can set your browser to automatically
            remove these cookies.
          </li>
          <li>
            <strong>Social sharing:</strong> These cookies enable you to share
            any of Our content that you may find interesting with friends via
            email and social media platforms.
          </li>
        </ul>

        <p>
          Please note that third parties (including, for example, advertising
          networks and providers of external services like web traffic analysis
          services) may also use cookies, over which We have no control. These
          cookies are likely to be analytical/performance cookies or targeting
          cookies.
        </p>

        <p>
          You can block cookies by activating the setting on your browser that
          allows you to refuse the setting of all or some cookies. However, if
          you use your browser settings to block all cookies (including
          essential cookies) you may not be able to access all or parts of our
          website.
        </p>

        <p>
          For more information about cookies, please visit:{' '}
          <a
            href="http://www.allaboutcookies.org"
            target="_blank"
            rel="noopener noreferrer"
          >
            http://www.allaboutcookies.org
          </a>
          .
        </p>

        <p>
          We store personal data in digital format on secure cloud servers and
          systems hosted in the European Union (EU) and the United Kingdom (UK).
          Where data is stored on servers located in the US, we rely on the
          Privacy Shield Framework to transfer this information. Access to
          personal data is highly restricted internally for approved business
          purposes only. Any personal data processed in paper form is securely
          filed at our office location in the UK.
        </p>

        <p className="mt-4">Last updated: 1 April 2023</p>
      </section>
    </>
  );
}
